/**
 * Billing attention (TMMPDM-0099). One read feeds every tab. A tab count is how many rows the
 * server sent for that list — absent or null counts as 0 — and is not a verdict.
 */
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { ConfirmDialog, ErrorState, PageHeader, useNotify } from '@/components/page';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy } from '@/api/errors';
import type { BillingAttention as BillingAttentionView } from '@/api/tmm/types';
import {
  Divergences,
  FeedStalled,
  ProductsToReview,
  SkippedEvents,
  StatementsNeedAttention,
  TaxClassificationRequired,
  UnhandledEvents,
} from './ai/BillingAttentionPanel';

const TABS = ['statements', 'divergences', 'parked', 'unhandled', 'products', 'tax'] as const;

type AttentionTab = (typeof TABS)[number];
type AttentionView = { tab: AttentionTab };

const VIEW: UrlSchema<AttentionView> = {
  tab: { default: 'statements', parse: oneOf(TABS) },
};

function countOf(rows: readonly unknown[] | null | undefined): number {
  return rows == null ? 0 : rows.length;
}

export function BillingAttention() {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data: BillingAttentionView | null;
    error: boolean;
    failure: unknown;
  }>({ key: '', data: null, error: false, failure: null });
  const key = String(reloadToken);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .billingAttention()
      .then((data) => {
        if (!cancelled) setState({ key, data, error: false, failure: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ key, data: null, error: true, failure: err });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const refetch = useCallback(() => setReloadToken((n) => n + 1), []);
  const current = state.key === key;
  const loading = !current;
  const error = current && state.error;
  const data = current ? state.data : null;

  const [declareOpen, setDeclareOpen] = useState(false);
  const [declareBusy, setDeclareBusy] = useState(false);
  const [declareError, setDeclareError] = useState<string | null>(null);
  const declare = async () => {
    setDeclareBusy(true);
    setDeclareError(null);
    try {
      const result = await adminApi.declareSkus();
      // Each product answers for itself; a product pay-api refused (`FAILED`) is not declared.
      if (result.skus.some((sku) => sku.outcome === 'FAILED')) {
        setDeclareError(
          t(
            'ai.attention.declare.someFailed',
            "Some products couldn't be declared. Try again; it's safe to repeat.",
          ),
        );
      } else {
        setDeclareOpen(false);
        notify.success(t('ai.attention.declare.done', 'Products declared.'));
      }
      refetch();
    } catch (err: unknown) {
      setDeclareError(
        errorCopy(err, t, t('ai.attention.declare.error', "Couldn't declare the products.")),
      );
    } finally {
      setDeclareBusy(false);
    }
  };

  const tabCount = (label: string, count: number) =>
    t('ai.attention.tabCount', '{{label}} ({{count, number}})', { label, count });

  return (
    <Box>
      <PageHeader
        title={t('page.billingAttention.title', 'Billing attention')}
        subtitle={t(
          'page.billingAttention.subtitle',
          'Billing problems that need a look. Each tab is one kind.',
        )}
      />
      <ConfirmDialog
        open={declareOpen}
        title={t('ai.attention.declare.title', "Send tmm's product list to pay-api again?")}
        consequence={t('ai.attention.declare.body', "It's safe to repeat.")}
        confirmLabel={t('ai.attention.declare.confirm', 'Declare products')}
        busy={declareBusy}
        error={declareError}
        onConfirm={() => void declare()}
        onCancel={() => setDeclareOpen(false)}
      />
      {error ? (
        <ErrorState
          error={state.failure}
          message={t('ai.attention.loadError', 'Could not load billing attention.')}
          onRetry={refetch}
        />
      ) : loading || !data ? (
        <Typography variant="body2" color="text.secondary">
          {t('common.loading', 'Loading')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {/* Absent (a tmm-api below 0126) reads as not stalled; the type stays as the YAML says. */}
          <FeedStalled stall={data.feedStalled ?? null} locale={locale} />
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
          >
            <FilterTabs
              ariaLabel={t('ai.attention.tabsAria', 'Billing attention sections')}
              active={view.tab}
              onChange={(next) => {
                const tab = oneOf(TABS)(next);
                if (tab) setView({ tab });
              }}
              tabs={[
                {
                  key: 'statements',
                  label: tabCount(
                    t('ai.attention.tab.statements', 'Statements'),
                    countOf(data.attentionStatements),
                  ),
                },
                {
                  key: 'divergences',
                  label: tabCount(
                    t('ai.attention.tab.divergences', 'Divergences'),
                    countOf(data.divergences),
                  ),
                },
                {
                  key: 'parked',
                  label: tabCount(
                    t('ai.attention.tab.parked', 'Parked payment events'),
                    countOf(data.skippedEvents),
                  ),
                },
                {
                  key: 'unhandled',
                  label: tabCount(
                    t('ai.attention.unhandled.title', 'Unhandled payment events'),
                    countOf(data.unhandledEvents),
                  ),
                },
                {
                  key: 'products',
                  label: tabCount(
                    t('ai.attention.skuReview.title', 'Products to review'),
                    countOf(data.skuReviewRequired),
                  ),
                },
                {
                  key: 'tax',
                  label: tabCount(
                    t('ai.attention.tab.tax', 'Tax classification'),
                    countOf(data.taxClassificationFailures),
                  ),
                },
              ]}
            />
            {view.tab === 'products' ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setDeclareError(null);
                  setDeclareOpen(true);
                }}
              >
                {t('ai.attention.declare.action', 'Declare products…')}
              </Button>
            ) : null}
          </Stack>
          {view.tab === 'statements' ? (
            <StatementsNeedAttention rows={data.attentionStatements} locale={locale} />
          ) : null}
          {view.tab === 'divergences' ? <Divergences rows={data.divergences} /> : null}
          {view.tab === 'parked' ? (
            <SkippedEvents rows={data.skippedEvents} locale={locale} />
          ) : null}
          {view.tab === 'unhandled' ? (
            <UnhandledEvents rows={data.unhandledEvents ?? []} locale={locale} />
          ) : null}
          {view.tab === 'products' ? (
            <ProductsToReview rows={data.skuReviewRequired ?? []} locale={locale} />
          ) : null}
          {view.tab === 'tax' ? (
            <TaxClassificationRequired
              rows={data.taxClassificationFailures ?? []}
              locale={locale}
            />
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
